import React from "react";
import { ReactFlow, Controls, applyNodeChanges, type NodeChange, ReactFlowProvider, useEdgesState, useNodesInitialized, useNodesState, useReactFlow } from "@xyflow/react";

import "./FlowChart.scss";
import "@xyflow/react/dist/style.css";
import { Chevron, Diamond, Group, MultiTriangle, Rectangle, RoundedRectangle, Triangle } from "./nodeTypes";
import { EdgeContentContext, NodeContentContext } from "./nodeTypes/common";
import { Bezier, Orthogonal, SimpleBezier, SmoothStep, Step, Straight } from "./edgeTypes";
import { useResizeDetector } from "react-resize-detector";
import { DocumentedComponent } from "../../../shared/components/DocumentedComponent";
import { getDataProps, removeDataProps } from "../../../shared/utilities";
import {
    reactFlowEdgeDefaults,
    mergeLayoutOptions,
    getLayoutedElements,
    rerouteEdges,
    mapInputData,
    type FlowChartLink,
    type Edge,
    type FlowNode,
    type FlowChartNode,
    type LayoutOptions,
    type HeadingSizes,
} from "./layout";

type FlowChartProps = {
    name?: string;
    nodes: FlowChartNode[];
    /**
     * Optional, and every field within it is too -- which is what callers have always sent.  The
     * documentation has described it that way since the component shipped; only the type said
     * otherwise, which is why every call site in the repo carried an `as any`.
     */
    layoutOptions?: LayoutOptions;
    getNodeContent?: (node: FlowChartNode) => React.JSX.Element;
    getEdgeContent?: (edge: FlowChartLink) => React.JSX.Element;
    /**
     * Whether links are re-routed continuously while a node is dragged, or only once it is dropped.
     * Optional - defaults to `true`.  Only meaningful when nodes can be dragged at all
     * (`nodesDraggable`, passed through to react-flow).
     */
    rerouteWhileDragging?: boolean;
};


const nodeTypes = {
    "default": Rectangle,
    "chevron": Chevron,
    "diamond": Diamond,
    "group": Group,
    "multi-triangle": MultiTriangle,
    "rectangle": Rectangle,
    "rounded-rectangle": RoundedRectangle,
    "triangle": Triangle
};
const edgeTypes = {
    "default": Bezier,
    "bezier": Bezier,
    "straight": Straight,
    "step": Step,
    "smoothstep": SmoothStep,
    "orthogonal": Orthogonal,
    "simplebezier": SimpleBezier,
};

const proOptions = { hideAttribution: true };
const fitViewOptions = { padding: 0.05, duration: 0 };
/** How many times to re-check for usable coordinates before revealing the chart regardless. */
const LAYOUT_ATTEMPT_LIMIT = 10;

/**
 * The rendered size of each group's heading text, in the element's own pixels rather than the
 * zoomed ones on screen.  react-flow measures a node's box, not the text inside it, and the layout
 * needs the text to size the group and keep links off the words.  An empty heading measures zero.
 */
const measureHeadings = (container: HTMLElement | null | undefined, nodes: FlowNode[]): HeadingSizes => {
    const sizes: HeadingSizes = new Map();
    if (!container) return sizes;

    for (const node of nodes) {
        if (node.type !== "group") continue;
        const heading = container.querySelector<HTMLElement>(`.react-flow__node[data-id="${CSS.escape(node.id)}"] .group-heading`);
        if (!heading || !(heading.textContent ?? "").trim()) {
            sizes.set(node.id, { width: 0, height: 0 });
            continue;
        }

        const range = document.createRange();
        range.selectNodeContents(heading);
        const text = range.getBoundingClientRect();
        const box = heading.getBoundingClientRect();
        // The screen box is the element's box times the viewport zoom; the ratio undoes it.
        const scale = box.width > 0 && heading.offsetWidth > 0 ? heading.offsetWidth / box.width : 1;
        sizes.set(node.id, { width: Math.ceil(text.width * scale), height: Math.ceil(text.height * scale) });
    }

    return sizes;
};

/**
 * Identity assigned to non-serialisable values met while keying the `nodes` prop.
 *
 * A node's `content` holds a `React.ReactNode`, and a React element carries an `_owner` chain that
 * is circular in development -- `JSON.stringify` throws on it outright.  Giving each such object a
 * stable number keyed off its identity keeps the behaviour the component has always had (a NEW
 * content element is a real change and must re-map) while letting the plain-JSON payloads that
 * templates actually send compare equal across renders.
 */
const elementIds = new WeakMap<object, number>();
let nextElementId = 0;

const stableKey = (value: unknown) => {
    try {
        return JSON.stringify(value, (_key, item) => {
            if (item != null && typeof item === "object" && ("$$typeof" in item || typeof (item as any).then === "function")) {
                const id = elementIds.get(item) ?? (elementIds.set(item, ++nextElementId), nextElementId);
                return `[[ref:${id}]]`;
            }

            return typeof item === "function" ? "[[fn]]" : item;
        });
    } catch {
        // Anything `JSON.stringify` still refuses -- a cycle that is not a React element, a
        // BigInt -- must not take the chart down with it: this runs in the render body, where a
        // throw is unrecoverable.  A unique key means "assume it changed", which is exactly the
        // behaviour the component had before it was keyed at all.
        return `[[unkeyable:${++nextElementId}]]`;
    }
};


const FLOW_CHART = (props: FlowChartProps) => {
    const { nodes: input, layoutOptions: _layoutOptions, getNodeContent, getEdgeContent, rerouteWhileDragging = true, ...rest } = props;
    // Memoised on the option VALUES, not the object identity.  The merge used to allocate a fresh
    // object every render while the reset effect below depended on `_layoutOptions` by identity,
    // so any parent re-render that rebuilt the prop restarted the whole render state machine and
    // threw away the layout.  Harmless-looking under Dagre; under an async engine it is also a
    // race.  The key is stringified because the option set is small and flat.
    const layoutOptionsKey = JSON.stringify(_layoutOptions ?? null);
    const layoutOptions = React.useMemo(() => mergeLayoutOptions(_layoutOptions), [layoutOptionsKey]);

    // `nodes` gets the same treatment, for the same reason: the reset effect below depended on the
    // prop by IDENTITY, so a parent writing `nodes={[...]}` inline -- which is how a template does
    // it -- restarted the state machine and re-ran the layout on every one of its renders, twice
    // over.  Keyed on the values instead, a re-render with structurally identical nodes is free.
    const nodesKey = React.useMemo(() => stableKey(input), [input]);
    const mapped = React.useMemo(
        () => mapInputData(input, getNodeContent, getEdgeContent),
        // `getNodeContent` / `getEdgeContent` are deliberately absent, matching the previous
        // behaviour: they are almost always fresh arrow functions, and depending on them would
        // reinstate exactly the thrash this memo exists to remove.
        [nodesKey]);

    const { fitView } = useReactFlow();
    const [nodes, setNodes, _onNodesChange] = useNodesState<FlowNode>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
    const { width, height, ref } = useResizeDetector();

    // Layout is asynchronous now, so results can arrive out of order: the Operational Planning
    // layout popover drives its sliders continuously, and each change resets the state machine.
    // A generation token means only the newest run is allowed to write, otherwise a slow layout
    // for one slider value lands on top of the result for a later one.
    const layoutRun = React.useRef(0);

    /** Heading sizes from the last layout, so a drag re-route sees the same title obstacles. */
    const headings = React.useRef<HeadingSizes>(new Map());

    // Callbacks below run on a timer or a promise continuation, so the values they closed over at
    // render time are already stale by the time they fire.  One ref, refreshed every render, is
    // what they read instead -- and it keeps `edges` out of the effect's dependency list, where
    // it would retrigger the state machine every time the layout wrote new edges.
    const latest = React.useRef({ nodes: [] as FlowNode[], edges: [] as Edge[], layoutOptions });

    /**
     * What became of one layout run.
     *
     * `superseded` is deliberately distinct from `failed`.  Both used to be the falsy half of a
     * boolean, and the caller read that as "no layout, show the nodes where they are" -- so a run
     * retired by a NEW INPUT advanced the machine to phase 2 on behalf of a graph that was about
     * to be replaced, and that write landed after the reset to phase 0 and won.  The chart then
     * revealed the un-laid-out nodes and never re-entered phase 0 for the new input.
     */
    type LayoutOutcome = "applied" | "failed" | "superseded";

    const layoutElements = async (nodes: FlowNode[], edges: Edge[]): Promise<LayoutOutcome> => {
        const run = ++layoutRun.current;

        try {
            headings.current = measureHeadings(ref.current, nodes);
            const layouted = await getLayoutedElements(nodes, edges, layoutOptions, undefined, headings.current);

            if (run !== layoutRun.current) {
                return "superseded";
            }

            setNodes([...layouted.nodes]);
            setEdges([...layouted.edges]);
            return "applied";
        } catch (error) {
            // The engine is loaded dynamically, so this covers a failed chunk fetch as well as a
            // graph it cannot lay out. Say so loudly: the nodes keep their pre-layout positions,
            // which means every one of them sits at the origin, and a silent failure here looks
            // like a rendering bug rather than a layout one.
            console.error("FlowChart: layout failed; nodes will not be positioned.", error);
        }

        return "failed";
    };

    // react-flow reports when every (non-hidden) node has been measured, which is exactly what
    // phase 0 used to poll for. Hidden nodes are never mounted and so never initialise, which is
    // why they are excluded rather than waited on.
    const nodesInitialized = useNodesInitialized({ includeHiddenNodes: false });

    // Refreshed after every commit rather than during render.  Writing a ref in the render body is
    // unsafe once rendering can be abandoned or replayed, and the callbacks reading this one run
    // on a timer or a promise continuation -- so post-commit is both correct and what they want.
    React.useEffect(() => {
        latest.current = { nodes, edges, layoutOptions };
    });

    // The component goes through a few phases before it can show a correct diagram, because it
    // cannot lay anything out until the browser has measured the rendered nodes.
    //
    // Phase 0 - Initial Render: wait for react-flow to measure the mounted nodes
    // Phase 1 - Layout: run the layout engine and check it produced usable coordinates
    // Phase 2 - Fit View: scale the result to fit the container
    // Phase 3 - Presentation: reveal the diagram
    //
    // Phase 2 stays a separate step on purpose: `fitView` reads react-flow's store, so it has to
    // run after React has committed the laid-out nodes, not straight after the layout resolves.
    const [renderState, setRenderState] = React.useState([0, 0]);
    React.useEffect(() => {
        const [phase, attempts] = renderState;
        let timeout: ReturnType<typeof setTimeout>;
        let cancelled = false;

        switch (phase) {
            case 0: {
                // No polling: the effect re-runs when `nodesInitialized` flips.
                if (!nodesInitialized || !nodes.length) {
                    return;
                }

                // Advance only once the layout has actually been applied. Layout is asynchronous
                // now, so firing and forgetting would let phase 1 validate the PRE-layout nodes --
                // all still stacked at the origin, all with finite coordinates, so the check
                // passes -- and phase 2 would then fit the view to a graph with no extent and
                // zoom straight to maxZoom.
                layoutElements(nodes, latest.current.edges).then((outcome) => {
                    // `cancelled` and the generation token guard the same thing from different
                    // sides, and they can disagree: a new input bumps the token in an effect whose
                    // deps this effect does not share, so its cleanup has not necessarily run by
                    // the time an older promise resolves.  A superseded run must write NOTHING --
                    // the reset that retired it is already on its way to phase 0.
                    if (cancelled || outcome === "superseded") {
                        return;
                    }

                    // Go on to phase 2 even when the layout FAILED. Stopping here left the
                    // container at `opacity: 0` for good: nothing rewrote `nodes`, so this
                    // effect never re-ran and phase 0 was never re-entered. Better to show the
                    // nodes where they are -- which is what the `catch` above says happens --
                    // than to show a permanently blank box.
                    setRenderState(outcome === "applied" ? [1, 0] : [2, 0]);
                });
                return () => { cancelled = true; };
            }
            case 1: {
                const laidOut = nodes.filter(n => !n.hidden).every(n =>
                    Number.isFinite(n.position?.x) && Number.isFinite(n.position?.y));

                if (!laidOut) {
                    // Bounded, unlike the original: this used to retry forever, so a graph the
                    // engine could not place left the chart invisible behind `opacity: 0` with
                    // nothing logged anywhere. Give up and show what we have instead.
                    if (attempts >= LAYOUT_ATTEMPT_LIMIT) {
                        console.warn("FlowChart: layout did not produce usable coordinates; rendering anyway.");
                        setRenderState([2, 0]);
                        break;
                    }

                    timeout = setTimeout(() => setRenderState([1, attempts + 1]), Math.min(2000, attempts * 100));
                    // `break`, not `return`: this is the ONE branch that arms `timeout`, and a
                    // `return` here skipped the `clearTimeout` cleanup below -- leaving the
                    // cleanup dead code and the retry free to fire after unmount, or against the
                    // pre-layout nodes of an input that had since been replaced.
                    break;
                }

                setRenderState([2, 0]);
                break;
            }
            case 2: {
                fitView(fitViewOptions);
                setRenderState([3, 0]);
                break;
            }
            case 3:
                break;
        }

        // The original effect had no cleanup, so pending timers fired after unmount -- which with
        // an awaited layout in the mix means a stale `setNodes`.
        return () => clearTimeout(timeout);
        // `edges` is deliberately absent: it is read through `latest` above, because the layout
        // rewrites it and a dependency here would restart the state machine on its own output.
    }, [renderState, nodesInitialized, nodes]);

    /** The animation frame in which the next live re-route runs, while one is scheduled. */
    const liveReroute = React.useRef<number | undefined>(undefined);
    // Which nodes the gesture in progress has moved.  Accumulated rather than read off the batch
    // that triggers a re-route: react-flow reports a drag as a stream of `position` changes and
    // only the last of them carries `dragging: false`, so no single batch describes everything the
    // gesture touched.  Drained when the node is dropped.
    const draggedNodes = React.useRef(new Set<string>());
    // The nodes as of the latest drag event.  `_onNodesChange` schedules a React update, but the
    // event that follows -- and an animation frame -- can arrive before it has committed, so
    // `latest.current.nodes` may still hold the positions from before the mouse moved.  Applying
    // the same changes to our own copy gives the re-route the positions the node is actually being
    // drawn at.  Cleared when the drag ends, after which the committed state is authoritative.
    const pendingNodes = React.useRef<FlowNode[] | undefined>(undefined);

    React.useEffect(() => {
        // Invalidate any layout still in flight.  A new INPUT retires a run just as surely as a
        // newer run does, and only the latter was bumping the token.  Resetting the state machine
        // below usually starts a run of its own, which bumped it as a side effect -- but not
        // always: phase 0 returns at its `!nodes.length` guard, so emptying the chart left the
        // in-flight run holding a current token, free to write the whole previous graph back over
        // the empty one.  Nothing rewrites `mapped` afterwards, so those nodes stayed.
        layoutRun.current++;

        // And any drag re-route still pending, for the same reason.  It carries the ids of the
        // nodes the gesture moved and no routes at all, so firing it against a graph that has just
        // been re-laid-out re-routes every link touching one of those ids against positions that
        // are already stale.  A drag overlapping a layout option change is not hypothetical: that
        // is the layout popover's sliders.
        cancelAnimationFrame(liveReroute.current);
        liveReroute.current = undefined;
        draggedNodes.current.clear();
        pendingNodes.current = undefined;

        setNodes(mapped.nodes);
        setEdges(mapped.edges);

        // Reset the state machine...
        setRenderState([0, 0]);
    }, [mapped, layoutOptions]);

    // Without this a live re-route scheduled by the last drag event fires after unmount and calls
    // `setEdges` on a dead component -- the same bug the render-state effect grew a cleanup for.
    React.useEffect(() => () => cancelAnimationFrame(liveReroute.current), []);

    /**
     * Rebuild the geometry of every link touching a node the current gesture has moved.
     *
     * This used to recompute the HANDLES only, which stopped meaning anything the moment the layout
     * engine started producing routes: the renderer draws `data.route` and ignores the handles
     * entirely, so a dragged node left its links behind, still joining the boxes it used to sit
     * between.  `rerouteEdges` rebuilds the geometry itself, through the same code path the layout
     * uses, and leaves every link the gesture has not touched exactly as it was.
     *
     * `edges` are read from the committed state even mid-drag.  That can be a frame behind, but the
     * only thing the re-route reads off an edge is the route it already has, and the links whose
     * routes are about to change are the ones touching a moved node -- which are recomputed from
     * scratch regardless.
     */
    const rerouteDragged = (final: boolean) => {
        const { edges, layoutOptions } = latest.current;
        const nodes = pendingNodes.current ?? latest.current.nodes;
        const moved = draggedNodes.current;

        if (final) {
            draggedNodes.current = new Set();
            pendingNodes.current = undefined;
        }

        try {
            setEdges(rerouteEdges(nodes, edges, layoutOptions, moved, headings.current));
        } catch (error) {
            // Nothing is awaiting this, so an exception here would surface only as an unhandled
            // error with the links silently left where the drag found them.
            console.error("FlowChart: could not re-route links after a drag.", error);
        }
    };

    const onNodesChange = (nodeChanges: NodeChange<FlowNode>[]) => {
        _onNodesChange(nodeChanges);

        const moves = nodeChanges.filter(change => change.type === "position");
        if (!moves.length) {
            return;
        }

        for (const change of moves) {
            draggedNodes.current.add(change.id);
        }
        pendingNodes.current = applyNodeChanges(nodeChanges, pendingNodes.current ?? latest.current.nodes);

        if (moves.some(change => !change.dragging)) {
            // Dropped: the gesture is over, so re-route now and let the committed state take over.
            cancelAnimationFrame(liveReroute.current);
            liveReroute.current = undefined;
            rerouteDragged(true);
            return;
        }

        // Still dragging.  One re-route per animation frame, however many move events arrive in
        // between: the node itself is drawn by react-flow at every event, so the links trailing by
        // at most a frame is invisible, and the router is the expensive half of the pair.
        if (rerouteWhileDragging && liveReroute.current === undefined) {
            liveReroute.current = requestAnimationFrame(() => {
                liveReroute.current = undefined;
                rerouteDragged(false);
            });
        }
    };

    React.useEffect(() => {
        // Effects close over the render that last created them, so this is the current phase even
        // though `renderState` is not a dependency -- the effect is meant to run on a resize, not
        // on a phase change.
        if (renderState[0] < 2) {
            return;
        }

        fitView(fitViewOptions);
    }, [width, height]);

    return (
        <div className="FlowChart-container" ref={ref} style={{ opacity: renderState[0] > 2 ? 1 : 0 }} {...getDataProps(props)}>
            {/*
              * The content callbacks are baked into each element's `data` when the input is
              * mapped, and the mapping memo cannot depend on them: a template writes them as
              * inline arrows, so depending on them would remap -- and so relayout -- on every
              * parent render.  That leaves the closure in `data` stale the moment the parent
              * rebuilds it over new state, which a selection highlight or a live reading does
              * constantly.  So the current pair is published here instead, and the content
              * components prefer it to `data.getContent`.
              *
              * This used to push the new arrow back into every node's and edge's `data`.
              * Correct, but `data` identity is what react-flow memoises on, so it re-rendered
              * the entire chart -- path maths included -- plus a second React pass, once per
              * parent render.  Nothing is rewritten now: a context update reaches the content
              * through those same memo boundaries, and a parent that keeps a callback stable
              * re-renders nothing at all.
              */}
            <NodeContentContext.Provider value={getNodeContent}>
                <EdgeContentContext.Provider value={getEdgeContent}>
                    <ReactFlow
                        fitViewOptions={fitViewOptions}
                        /*
                         * Load-bearing for the crossing indicators.  v12 gives every edge its own
                         * `<svg>` and, by default, a z-index that includes the nesting depth of
                         * its endpoints -- so an edge inside a band painted over one outside it,
                         * whatever their order in the array.  `crossings.ts` assigns each crossing
                         * to the link declared LAST, which only interrupts the other link if array
                         * order really is paint order.  Manual mode hands each edge its own
                         * `zIndex`, which nothing here sets, so they are all zero and the DOM
                         * decides.  Node layering is untouched: `FlowChart.scss` pins nodes,
                         * groups and the edge layer with `!important` regardless.
                         */
                        zIndexMode="manual"
                        defaultEdgeOptions={reactFlowEdgeDefaults}
                        nodesDraggable={false}
                        nodesConnectable={false}
                        elementsSelectable={false}
                        minZoom={0.1}
                        maxZoom={10}
                        {...removeDataProps(rest)}
                        nodes={nodes}
                        edges={edges}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        proOptions={proOptions}
                    >
                        <Controls showInteractive={false} fitViewOptions={fitViewOptions} />
                    </ReactFlow>
                </EdgeContentContext.Provider>
            </NodeContentContext.Provider>
        </div>
    );
};

function FlowChart(props: FlowChartProps) {
    return (
        <ReactFlowProvider>
            <FLOW_CHART {...props} />
        </ReactFlowProvider>
    );
}

(FlowChart as DocumentedComponent).metadata = {
    description: "The `FlowChart` component is used to render process flow charts.  It is a wrapper around the [react-flow](https://reactflow.dev) library, and provides a more user-friendly interface for creating flow charts.",
    isSelfClosing: true,
    attributes: [
        { name: "name", type: "string", description: "If provided, the underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value. Optional - no default value." },
        {
            name: "nodes",
            type: "object",
            exactType: "{ id: string, type?: string, hidden?: boolean, links?: { id?: string, from?: string, to?: string, type?: string, routing?: 'elk' | 'direct', crossing?: 'none' | 'casing' | 'hop', color?: string, width?: number, className?: string, style?: React.CSSProperties, animated?: boolean, markerStart?: 'arrow' | 'arrowclosed' | { type: 'arrow' | 'arrowclosed', width?: number, height?: number, color?: string }, markerEnd?: 'arrow' | 'arrowclosed' | { type: 'arrow' | 'arrowclosed', width?: number, height?: number, color?: string }, positionStart?: 'top' | 'right' | 'bottom' | 'left', positionEnd?: 'top' | 'right' | 'bottom' | 'left', label?: string, content?: React.ReactNode, template?: string, excludeFromLayout?: boolean }[], children?: Node[], className?: string, style?: React.CSSProperties, label?: string, content?: React.ReactNode, template?: string, fillPercentage?: number, x?: number, y?: number }[]",
            description: `The content to render in the chart.  See below for the definition of \`Node\`:

### \`Node\` props:

| Name | Type | Description |
|------|------|-------------|
| \`id\` | \`string\` | A unique identifier for the node.  Used primarily in link definitions, to define the from/to pairs. |
| \`type\` | \`string\` | The type of the node.  Determines which shape is rendered for this node.  Optional - defaults to \`default\`, which draws a rectangle.  Available options: \`chevron\`, \`diamond\`, \`group\`, \`multi-triangle\`, \`rectangle\`, \`rounded-rectangle\` and \`triangle\`.  An unrecognised value falls back to \`default\` rather than failing. |
| \`className\` | \`string\` | Additional CSS classes to apply to the node.  Optional - defaults to an empty string. |
| \`hidden\` | \`boolean\` | If \`true\`, this node is not displayed to the end-user.  It still occupies a place in the layout, so it can be used to pad a group out or to nudge the flow along, but because it is never rendered it has no measured size and so reserves no space of its own.  Optional - defaults to \`false\`. |
| \`links\` | \`Link[]\` | The links which connect this node to other nodes.  Optional - defaults to an empty array.  See below for the definition of \`Link\`. |
| \`children\` | \`Node[]\` | The child nodes of this node.  Turns the current node into a group, containing the specified children.  Optional - defaults to an empty array. |
| \`label\` | \`string\` | The text to display in the node.  Optional - defaults to \`null\`. |
| \`content\` | \`JSX.Element\` | The content to display in the node.  Optional - defaults to \`null\`. |
| \`template\` | \`string\` | The template to use for this node.  This string is passed through to an embedded \`ContentRenderer\` component.  Optional - defaults to \`null\`. |
| \`fillPercentage\` | \`number\` | The percentage of vertical space to fill using the \`--fill-color\` value.  Optional - defaults to the entire node being filled. |
| \`x\` | \`number\` | The x-coordinate of the node.  Providing a coordinate for any node switches the whole chart to manual placement, as though \`layoutOptions.layout\` were set to \`manual\`.  Prefer setting that explicitly: the implicit behaviour is kept for compatibility but is easy to trigger by accident.  Optional - defaults to \`null\`. |
| \`y\` | \`number\` | The y-coordinate of the node.  See \`x\`.  Optional - defaults to \`null\`. |


### \`Link\` props:

| Name | Type | Description |
|------|------|-------------|
| \`from\` | \`string\` | The id of the node from which this link originates.  If not specified, the \`id\` of the associated node is used, turning this into an outbound link. |
| \`to\` | \`string\` | The id of the node to which this link connects.  If not specified, the \`id\` of the associated node is used, turning this into an inbound link. |
| \`id\` | \`string\` | Distinguishes two links between the same pair of nodes.  Without one, a link is keyed on its \`from\` and \`to\`, and a second link between the same ordered pair is discarded.  Optional - defaults to \`"{from}-{to}"\`. |
| \`type\` | \`string\` | How this link is drawn.  Every link is routed by the layout engine, so this selects how the corners of that route are treated rather than choosing a path of its own.  Optional - defaults to \`smoothstep\`.  Possible values are \`orthogonal\` and \`smoothstep\` (rounded corners), \`step\` (square corners), \`bezier\` and \`simplebezier\` (the route, smoothed) and \`straight\` (the route is discarded in favour of a direct line between the two nodes). |
| \`routing\` | \`string\` | Whether this link follows the route computed for it, or is drawn as a plain point-to-point path.  Optional - defaults to \`elk\`.  Possible values are \`elk\` and \`direct\`.  A \`straight\` link ignores the route regardless. |
| \`crossing\` | \`string\` | How this link is drawn where its path crosses another link's.  Links are painted in the order they are declared, so a link always crosses *over* the links declared before it.  Optional - defaults to \`none\`.  Possible values are \`none\` (the paths simply overlap, with nothing to show which passes over which), \`casing\` (a short background-coloured break is drawn beneath this link at each crossing, interrupting the link it crosses) and \`hop\` (a small semi-circular bridge is drawn in this link's own path at each crossing).  The break is coloured from the \`--edge-casing-color\` CSS variable on \`.FlowChart-container\`, which defaults to the chart background and can be overridden by page CSS.  A \`straight\` link never receives one. |
| \`excludeFromLayout\` | \`boolean\` | If \`true\`, this link will not be included in the layout calculations.  Optional - defaults to \`false\`. |
| \`color\` | \`string\` | The color of the link.  Can be an RGB string, a named color or a CSS variable lookup.  Optional - defaults to dark grey. |
| \`width\` | \`number\` | The width of the link path (in pixels).  Optional - defaults to \`4\` pixels. |
| \`style\` | \`Object\` | Additional CSS styles rules to apply to the link.  Optional - defaults to an empty object. |
| \`animated\`| \`boolean\` | If \`true\`, the link is animated.  Optional - defaults to \`false\`. |
| \`markerStart\` | \`string\` \| \`Object\` | The marker to use at the start of the link.  Optional - defaults to \`null\`, i.e. no marker.  Either a name (\`arrow\` or \`arrowclosed\`) or an object of the form \`{ type, width, height, color }\` for full control. |
| \`markerEnd\` | \`string\` \| \`Object\` | The marker to use at the end of the link.  Optional - **defaults to \`arrowclosed\`**, so links are arrow-headed unless told otherwise; pass \`null\` for none.  Takes the same values as \`markerStart\`. |
| \`positionStart\` | \`"top" \| "right" \| "bottom" \| "left"\` | The side of the "from" node that this link leaves by.  The router is told about it before routing, so the route genuinely starts there rather than being redirected afterwards.  Honoured while doing so costs little; where it would force the link across another link or the long way round, the router uses the side it would have chosen and says so in the browser console, once per link.  Optional - defaults to the side being chosen by the router. |
| \`positionEnd\` | \`"top" \| "right" \| "bottom" \| "left"\` | The side of the "to" node that this link arrives at.  See \`positionStart\`.  Optional - defaults to the side being chosen by the router. |
| \`label\` | \`string\` | The text to display on the link.  Optional - defaults to \`null\`. |
| \`content\` | \`JSX.Element\` | The content to display on the link.  Optional - defaults to \`null\`. |
| \`template\` | \`string\` | The template to use for this link.  This string is passed through to an embedded \`ContentRenderer\` component.  Optional - defaults to \`null\`.  Deprecated: prefer \`content\`. |

Note that a link is identified by its \`from\` and \`to\` unless it is given an explicit \`id\`.  A link whose \`from\` and \`to\` are the same node is ignored, as is a second link between the same ordered pair of nodes.
`},
        {
            name: "layoutOptions",
            type: "object",
            exactType: "{ layout?: 'auto' | 'manual', corners?: 'rounded' | 'sharp' | 'mixed', mergeLinks?: boolean, rankdir?: 'TB' | 'BT' | 'LR' | 'RL', align?: 'UL' | 'UR' | 'DL' | 'DR', ranker?: 'network-simplex' | 'longest-path' | 'tight-tree', nodesep?: number, edgesep?: number, ranksep?: number, includeInterGroupLinksInLayout?: boolean, fromHandleMappings?: { 'left-straight': 'left', 'left-up': 'left', 'left-down': 'bottom', 'right-straight': 'right', 'right-up': 'right', 'right-down': 'right', 'straight-up': 'top', 'straight-down': 'bottom' }, toHandleMappings?: { 'left-straight': 'right', 'left-up': 'bottom', 'left-down': 'top', 'right-straight': 'left', 'right-up': 'left', 'right-down': 'top', 'straight-up': 'bottom', 'straight-down': 'top' }, elkOptions?: Record<string, string> }",
            description: `The layout options to use when rendering the chart.  See below for the definition of \`LayoutOptions\`:

### \`LayoutOptions\` props:

| Name | Type | Description |
|------|------|-------------|
| \`rankdir\` | \`string\` | The direction in which the chart is laid out.  Optional - defaults to \`LR\`.  Possible values are \`TB\` (top-to-bottom), \`BT\` (bottom-to-top), \`LR\` (left-to-right) and \`RL\` (right-to-left). |
| \`align\` | \`string\` | How nodes are aligned within a level, across the direction of flow.  Optional - defaults to centred.  Possible values are \`UL\` (up-left), \`UR\` (up-right), \`DL\` (down-left) and \`DR\` (down-right). |
| \`ranker\` | \`string\` | The ranking algorithm used to decide which level each node belongs to.  Optional - defaults to \`network-simplex\`, which keeps the chart compact.  \`longest-path\` instead pushes every node as late as its links allow, which spreads a chart with uneven branch lengths out to its full depth.  **\`tight-tree\` is deprecated and will be removed:** the current layout engine has no equivalent, so it behaves identically to \`network-simplex\` and using it logs a warning. |
| \`nodesep\` | \`number\` | The separation between nodes at the same level, which share the same parent (in pixels).  Optional - defaults to \`32\`. |
| \`edgesep\` | \`number\` | The minimum gap between two link paths running alongside each other (in pixels).  It only takes effect where links actually compete for room, so on a sparse chart it may make no visible difference until set well above the default.  Optional - defaults to \`32\`. |
| \`ranksep\` | \`number\` | The separation between successive levels of the chart (in pixels).  In a left-to-right layout this is the horizontal gap between one column of nodes and the next.  Optional - defaults to \`32\`. |
| \`corners\` | \`"rounded" \| "sharp" \| "mixed"\` | How the corners of every link on the chart are drawn.  A chart draws all its links one way: \`rounded\` corners at one radius (the tightest any route on the chart can carry, so every corner matches), or \`sharp\` ones.  \`mixed\` lets each link's \`type\` decide instead, which is how earlier versions behaved.  Optional - defaults to \`rounded\`. |
| \`mergeLinks\` | \`boolean\` | Whether links ARRIVING at the same node are merged into a single trunk.  With this on, links that reach a node by the same side meet it at the same point and share the run leading up to it, so a fan-in reads as one arrow rather than several landing side by side.  A link the router brings in by a different side keeps its own arrival, since merging it would mean routing it badly to get there.  Links LEAVING a node are not affected: merging a fan-out drags a link to a near neighbour onto a trunk placed for the farthest one, which costs it a straight path for nothing.  Note that merged links overlap along the shared run, so where they differ in colour or width only the last one declared is visible over it.  Optional - defaults to \`false\`. |
| \`includeInterGroupLinksInLayout\` | \`boolean\` | By default, links between nodes which belong to different groups are not included in the layout calculation (since those break the linear flow of the layout).  This flag can be used to include these links, which then influences how the groups are positioned relative to one another.  Either way the link is still drawn: a link left out of the layout is given a path that runs between the groups and around any nodes in the way.  Optional - defaults to \`false\`. |
| \`layout\` | \`string\` | Whether the chart lays itself out or honours the \`x\`/\`y\` on each node.  Optional - defaults to \`auto\`, unless a node carries a coordinate, in which case the whole chart falls back to \`manual\`.  Possible values are \`auto\` and \`manual\`.  Setting this explicitly is preferred: the implicit fallback cannot express "manual, at the origin", and one stray coordinate switches the entire chart. |
| \`elkOptions\` | \`Object\` | Advanced.  Raw [ELK layout options](https://eclipse.dev/elk/reference/options.html), merged over everything derived from the fields above.  Keys are ELK option ids and values are strings.  Not covered by the compatibility guarantees that apply to the other fields.  Optional - defaults to an empty object. |
| \`fromHandleMappings\` | \`HandleMapping\` | **Deprecated: only applies when \`layout\` is \`manual\`.**  With an automatic layout the router chooses which side each link leaves by, so this table is not consulted.  In a manual layout an entry that differs from the default pins the side the way \`positionStart\` does, and is set aside with the same console warning where honouring it would cost a crossing or a long detour; a default entry leaves the choice to the router.  Use a link's \`positionStart\` to pin a side instead.  The key of the dictionary is a string indicating the position of the target node relative to the source node.  This is of the form \`"{x-direction}-{y-direction}"\`, where \`x-direction\` is one of \`left\`, \`right\` or \`straight\` and \`y-direction\` is one of \`up\`, \`down\` or \`straight\`.  Optional - defaults to \`{ "right-down": "right", "right-up": "right", "left-down": "left", "left-up": "left", "straight-up": "top", "straight-down": "bottom" }\`. |
| \`toHandleMappings\` | \`HandleMapping\` | **Deprecated: see \`fromHandleMappings\`.**  The same, for the side a link arrives by, where the link has no \`positionEnd\`.  The key of the dictionary is a string indicating the position of the target node relative to the source node.  This is of the form \`"{x-direction}-{y-direction}"\`, where \`x-direction\` is one of \`left\`, \`right\` or \`straight\` and \`y-direction\` is one of \`up\`, \`down\` or \`straight\`.  Optional - defaults to \`{ "left-straight": "right", "left-up": "bottom", "left-down": "top", "right-straight": "left", "right-up": "left", "right-down": "top", "straight-up": "bottom", "straight-down": "top" }\`. |

### \`HandleMapping\` fields:

| Name | Type | Description |
|------|------|-------------|
| \`left-straight\` | \`"left"\` \| \`"right"\` \| \`"top"\` \| \`"bottom"\` | The handle to use when the target node is directly to the left of the source node. |
| \`left-up\` | \`"left"\` \| \`"right"\` \| \`"top"\` \| \`"bottom"\` | The handle to use when the target node is above and to the left of the source node. |
| \`left-down\` | \`"left"\` \| \`"right"\` \| \`"top"\` \| \`"bottom"\` | The handle to use when the target node is below and to the left of the source node. |
| \`right-straight\` | \`"left"\` \| \`"right"\` \| \`"top"\` \| \`"bottom"\` | The handle to use when the target node is directly to the right of the source node. |
| \`right-up\` | \`"left"\` \| \`"right"\` \| \`"top"\` \| \`"bottom"\` | The handle to use when the target node is above and to the right of the source node. |
| \`right-down\` | \`"left"\` \| \`"right"\` \| \`"top"\` \| \`"bottom"\` | The handle to use when the target node is below and to the right of the source node. |
| \`straight-up\` | \`"left"\` \| \`"right"\` \| \`"top"\` \| \`"bottom"\` | The handle to use when the target node is directly above the source node. |
| \`straight-down\` | \`"left"\` \| \`"right"\` \| \`"top"\` \| \`"bottom"\` | The handle to use when the target node is directly below the source node. |
`},
        {
            name: "getNodeContent",
            type: "function",
            // Shapes are spelled out inline rather than named. `FlowChartLink[]`/`FlowChartNode[]`
            // were meaningless to a template author -- those types are deliberately not exported,
            // so there was nothing to look up. `children` is genuinely absent from what the
            // callback receives; it would duplicate the whole subtree onto every node.
            exactType: "(node: { id: string, parentId?: string, type: string, hidden: boolean, links: { from?: string, to?: string, type?: string, label?: string }[], label: string, content: JSX.Element, template?: string, fillPercentage: number, x: number, y: number }) => JSX.Element",
            template: "getNodeContent={(node) => $1}",
            description: "A function which returns the content to render in a node.  Supplying this replaces the default rendering for every node, so return \`null\` for the nodes you want handled normally.  Optional - when it is not supplied, or returns nothing, the first non-null \`template\`, \`content\` or \`label\` field of the node is rendered, in that order."
        },
        {
            name: "getEdgeContent",
            type: "function",
            // `source`/`target` are the same values as `from`/`to`, both present so that either
            // spelling works. `positionStart`/`positionEnd` were given as `HandlePosition`, a name
            // a template author has no way to resolve; the four sides are written out instead.
            // The markers are the NORMALISED form: a link may be authored with either the name or
            // the object, but `normaliseMarker` has resolved it to an object by the time a
            // callback sees it, so the name is not one of the things that can arrive here.
            exactType: "(edge: { id: string, from: string, to: string, source: string, target: string, type: string, color: string, width: number, className: string, style: React.CSSProperties, animated: boolean, markerStart: { type: 'arrow' | 'arrowclosed', width?: number, height?: number, color: string }, markerEnd: { type: 'arrow' | 'arrowclosed', width?: number, height?: number, color: string }, positionStart: 'top' | 'right' | 'bottom' | 'left', positionEnd: 'top' | 'right' | 'bottom' | 'left', routing: 'elk' | 'direct', crossing: 'none' | 'casing' | 'hop', label: string, content: JSX.Element, template?: string, excludeFromLayout: boolean }) => JSX.Element",
            template: "getEdgeContent={(edge) => $1}",
            description: "A function which returns the content to render in a link.  Supplying this replaces the default rendering for every link, so return \`null\` for the links you want handled normally.  Optional - when it is not supplied, or returns nothing, the first non-null \`template\`, \`content\` or \`label\` field of the link is rendered, in that order.  The object also carries the geometry the chart computed for the link -- \`route\` (the points of the drawn path) and \`hops\` (its crossing indicators).  Both are read-only and internal: they are not covered by the compatibility guarantees that apply to the fields listed above."
        },
        {
            name: "rerouteWhileDragging",
            type: "boolean",
            description: "Whether the links attached to a node are re-routed continuously while it is being dragged, or only once it is dropped.  Optional - defaults to \`true\`.  Turning it off trades the live feedback for less work per frame, which may matter on a very large chart.  Only has an effect when nodes can be dragged, which they cannot by default: pass \`nodesDraggable\` (forwarded to react-flow) to enable that.  A dragged position is not persisted; a change to \`nodes\` or \`layoutOptions\` re-runs the layout."
        },
    ]
};


export { FlowChart };
